import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { buildPaymentPayload, PaymentGWPayload } from '../services/payments';
import { useLanguage } from '../context/LanguageContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';

const PayRedirect: React.FC = () => {
  const { orderId } = useParams();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [payload, setPayload] = useState<PaymentGWPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const requestedRef = useRef(false);
  const paymentPage = import.meta.env.VITE_PAYMENT_PAGE_URL || "https://ecg.test.upc.ua/rbrs/enter";

  useEffect(() => {
    if (!orderId) {
      setError(t('checkout.missingOrder'));
      return;
    }
    if (requestedRef.current) return;
    requestedRef.current = true;

    const run = async () => {
      try {
        const data = await buildPaymentPayload(orderId);
        setPayload(data);
      } catch (e) {
        console.error(e);
        setError(t('checkout.prepareError'));
      }
    };

    run();
  }, [orderId, t]);

  // Submit only after payload is set and form is rendered
  useEffect(() => {
    if (!payload) return;
    if (!formRef.current) return;
    formRef.current.submit();
  }, [payload]);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 rounded-xl shadow-sm text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-3">
            {t('checkout.paymentTitle')}
          </h1>

          {error ? (
            <>
              <p className="text-sm text-red-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/buy-tokens')}>
                {t('checkout.backToTokens')}
              </Button>
            </>
          ) : !payload ? (
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <LoadingSpinner size="sm" color="neutral" />
              <p className="text-sm">{t('checkout.preparing')}</p>
            </div>
          ) : (
            <>
              <form ref={formRef} method="post" action={paymentPage}>
                <input type="hidden" name="Version" value={payload.version} />
                <input type="hidden" name="MerchantID" value={payload.merchantId} />
                <input type="hidden" name="TerminalID" value={payload.terminalId} />
                <input type="hidden" name="TotalAmount" value={String(payload.totalAmount)} />
                <input type="hidden" name="Currency" value={String(payload.currency)} />
                <input type="hidden" name="locale" value={payload.locale} />
                <input type="hidden" name="PurchaseTime" value={payload.purchaseTime} />
                <input type="hidden" name="OrderID" value={payload.orderId} />
                <input type="hidden" name="Delay" value={String(payload.delay)} />
                <input type="hidden" name="Signature" value={payload.signature} />
                <noscript>
                  <button type="submit">{t('checkout.continue')}</button>
                </noscript>
              </form>
              <p className="text-sm text-gray-600 mt-3">
                {t('checkout.redirecting')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayRedirect;

